import { Injectable } from '@angular/core';
import {
  LongestParkingItem,
  ParkingTransactionChartDto,
  ParkingTransactionChartItemDto,
  ParkingTransactionDashboardStats,
  ParkingTransactionLongestItemDto,
  ParkingTransactionSummaryDto,
  ParkingTransactionTableDto,
} from '../../models/parking/transaction-parking.model';
import { ParkingTransactionResponse } from '../../models/parking.model';

@Injectable({
  providedIn: 'root',
})
export class MapperTransactionParkingService {
  mapTableFromDto(dto: ParkingTransactionTableDto): {
    items: ParkingTransactionResponse[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    summary: ParkingTransactionDashboardStats | null;
    gateOptions: string[];
    vehicleTypeOptions: string[];
  } {
    return {
      items: dto.data ?? [],
      total: dto.total ?? 0,
      page: dto.page ?? 1,
      pageSize: dto.pageSize ?? 20,
      totalPages: dto.totalPages ?? 0,
      summary: dto.summary ? this.mapSummary(dto.summary) : null,
      gateOptions: dto.gateOptions ?? [],
      vehicleTypeOptions: dto.vehicleTypeOptions ?? [],
    };
  }

  mapChartItems(dto: ParkingTransactionChartDto): ParkingTransactionResponse[] {
    return (dto.items ?? []).map(item => this.mapChartItem(item));
  }

  mapSearchField(type: string): string {
    return type;
  }

  mapSortColumn(column: string): string {
    return column.toLowerCase();
  }

  mapStatusFilter(status: 'all' | 'active' | 'completed'): string | undefined {
    return status === 'all' ? undefined : status;
  }

  mapSelectedNames(selected: Record<string, boolean>): string | undefined {
    const names = Object.keys(selected).filter(key => selected[key]);
    return names.length > 0 ? names.join(',') : undefined;
  }

  private mapChartItem(item: ParkingTransactionChartItemDto): ParkingTransactionResponse {
    return {
      id: item.id,
      cardCode: '',
      cardName: '',
      entryLicense: item.entryLicense ?? '',
      exitLicense: '',
      entryProvince: '',
      exitProvince: '',
      entryDate: item.entryDate ?? null,
      exitDate: null,
      entryGate: 0,
      entryGateName: '',
      exitGate: null,
      exitGateName: '',
      vehicleTypeId: 0,
      vehicleTypeName: item.vehicleTypeName ?? '',
      entryPath1: '',
      exitPath1: '',
      oldTicket: '',
      subscriptionId: 0,
      subscriptionName: '',
      siteId: 0,
      siteName: '',
      member: false,
    };
  }

  private mapSummary(summary: ParkingTransactionSummaryDto): ParkingTransactionDashboardStats {
    return {
      totalTransactions: summary.totalTransactions ?? 0,
      activeCount: summary.activeCount ?? 0,
      completedCount: summary.completedCount ?? 0,
      longestParkingList: (summary.longestParkingList ?? []).map(item => this.mapLongestItem(item)),
    };
  }

  private mapLongestItem(item: ParkingTransactionLongestItemDto): LongestParkingItem {
    const durationMs = item.durationMs ?? 0;
    return {
      license: item.entryLicense || '-',
      durationText: this.formatDurationMs(durationMs),
      isOvertime: durationMs > 24 * 60 * 60 * 1000,
      vehicleType: item.vehicleTypeName || '',
      entryTime: item.entryDate ?? null,
      isActive: item.isActive ?? false,
      siteName: item.siteName || '-',
    };
  }

  private formatDurationMs(diffMs: number): string {
    if (diffMs < 0) return '-';
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (diffHrs > 24) {
      const days = Math.floor(diffHrs / 24);
      const remainHrs = diffHrs % 24;
      return `${days} วัน ${remainHrs} ชม.`;
    }
    return `${diffHrs} ชม. ${diffMins} นาที`;
  }
}
